import type {
  FilterPayloadNode,
  GridColumnInfo,
  GridFilter,
  GridFilterGroupState,
  GridFilterOperator,
} from '../types'

export function isFilterableColumn(column: GridColumnInfo) {
  return (
    column.type !== 'checkbox' &&
    column.type !== 'seq' &&
    Boolean(column.field)
  )
}

export function getFirstFilterableColumn(columns: GridColumnInfo[]) {
  return columns.find(isFilterableColumn)
}

export function isEmptyFilterValue(val: unknown) {
  return val === null || val === undefined || val === ''
}

export function isActiveFilter(cond: GridFilter) {
  if (!cond.field) {
    return false
  }
  if (cond.operator === 'empty' || cond.operator === 'notEmpty') {
    return true
  }
  return !isEmptyFilterValue(cond.value)
}

export function isValuelessOperator(operator: GridFilterOperator) {
  return operator === 'empty' || operator === 'notEmpty'
}

function buildConditionPayload(cond: GridFilter, index: number): FilterPayloadNode {
  const node: FilterPayloadNode = {
    field: cond.field,
    operator: cond.operator,
    value: cond.value,
  }
  if (index > 0) {
    node.logic = cond.logic || 'and'
  }
  return node
}

/** 为兄弟节点附加组间逻辑（index > 0 时写入 logic） */
function attachSiblingLogic(
  payload: FilterPayloadNode,
  index: number,
  siblingLogic?: string
): FilterPayloadNode {
  if (index === 0) {
    return payload
  }
  return {
    ...payload,
    logic: siblingLogic || 'or',
  }
}

function appendPayloadChild(
  children: FilterPayloadNode[],
  node: FilterPayloadNode,
  siblingLogic = 'and'
) {
  if (children.length === 0) {
    const { logic: _logic, ...firstNode } = node
    children.push(firstNode)
    return
  }
  children.push({
    ...node,
    logic: node.logic || siblingLogic,
  })
}

function buildGroupPayload(group: GridFilterGroupState): FilterPayloadNode | null {
  const activeConditions = group.conditions.filter(isActiveFilter)
  if (!activeConditions.length) {
    return null
  }
  if (activeConditions.length === 1) {
    return buildConditionPayload(activeConditions[0], 0)
  }
  return {
    type: 'group',
    children: activeConditions.map((cond, index) => buildConditionPayload(cond, index)),
  }
}

/**
 * 将分组筛选状态序列化为查询 payload。
 * - 单组单条件：扁平对象（兼容旧接口）
 * - 单组多条件：扁平数组（兼容旧接口）
 * - 多组：嵌套 group 树，组间用 groupLogic 连接
 */
export function buildFilterPayload(
  filterGroups: GridFilterGroupState[] | null | undefined
): FilterPayloadNode | FilterPayloadNode[] | null {
  const groups = Array.isArray(filterGroups) ? filterGroups : []
  const groupPayloads = groups
    .map(buildGroupPayload)
    .filter((item): item is FilterPayloadNode => item != null)

  if (!groupPayloads.length) {
    return null
  }

  if (groupPayloads.length === 1) {
    const only = groupPayloads[0]
    if (only.type === 'group' && only.children?.length) {
      return only.children
    }
    return only
  }

  const rootLogic = groups[1]?.groupLogic || 'or'
  return {
    type: 'group',
    children: groupPayloads.map((payload, index) =>
      attachSiblingLogic(payload, index, groups[index]?.groupLogic || rootLogic)
    ),
  }
}

/** 合并 headId 等系统条件与用户筛选 payload，写入查询 params */
export function resolveQueryFiltersParam(
  userFilters: FilterPayloadNode | FilterPayloadNode[] | null,
  systemFilters: FilterPayloadNode[] = []
): string | undefined {
  const hasUser = userFilters != null && (
    Array.isArray(userFilters) ? userFilters.length > 0 : true
  )
  const hasSystem = systemFilters.length > 0

  if (!hasUser && !hasSystem) {
    return undefined
  }
  if (!hasUser) {
    return JSON.stringify(systemFilters.length === 1 ? systemFilters[0] : systemFilters)
  }
  if (!hasSystem) {
    return JSON.stringify(userFilters)
  }

  const userNodes = Array.isArray(userFilters) ? userFilters : [userFilters]
  const children: FilterPayloadNode[] = []
  for (const systemFilter of systemFilters) {
    appendPayloadChild(children, systemFilter)
  }
  for (const userNode of userNodes) {
    appendPayloadChild(children, userNode)
  }

  if (children.length === 1) {
    return JSON.stringify(children[0])
  }
  return JSON.stringify({ type: 'group', children })
}

function normalizeFilterPayload(
  payload: FilterPayloadNode | FilterPayloadNode[] | null
): FilterPayloadNode | FilterPayloadNode[] | null {
  if (payload == null) {
    return null
  }
  if (Array.isArray(payload)) {
    const next = payload
      .map((node) => normalizeFilterPayload(node) as FilterPayloadNode | null)
      .filter((node): node is FilterPayloadNode => node != null)
    return next.length ? next : null
  }
  if (payload.type === 'group' && payload.children?.length) {
    const children = payload.children
      .map((child) => normalizeFilterPayload(child) as FilterPayloadNode | null)
      .filter((child): child is FilterPayloadNode => child != null)
    if (!children.length) {
      return null
    }
    if (children.length === 1) {
      const only = children[0]
      if (payload.logic && !only.logic) {
        return { ...only, logic: payload.logic }
      }
      return only
    }
    return { ...payload, children }
  }
  return payload
}

/** 从 payload 中排除指定字段的条件（列去重查询用） */
export function excludeFieldFromFilterPayload(
  payload: FilterPayloadNode | FilterPayloadNode[] | null,
  field: string
): FilterPayloadNode | FilterPayloadNode[] | null {
  if (payload == null) {
    return null
  }
  if (Array.isArray(payload)) {
    const next = payload.filter((node) => node.field !== field)
    return normalizeFilterPayload(next.length ? next : null)
  }
  if (payload.type === 'group' && payload.children?.length) {
    const children = payload.children
      .map((child) => excludeFieldFromFilterPayload(child, field))
      .filter((child): child is FilterPayloadNode => child != null)
    return normalizeFilterPayload(children.length ? { ...payload, children } : null)
  }
  if (payload.field === field) {
    return null
  }
  return payload
}
